import { LitElement, html, css, nothing } from 'lit';
import { PRINT_STYLES } from './print-styles.js';
import { repeat } from 'lit/directives/repeat.js';
import { StoreMixin, store } from '../store.js';
import { api } from '../api.js';
import { navigate, setFilters } from '../router.js';
import { apiParams } from '../lib/filters.js';
import { STATUS_STATES } from '../lib/palette.js';
import {
  DAY_GRAIN_WEEKS,
  HEATMAP_DIMENSIONS,
  HEATMAP_PARAM_KEYS,
  ROW_NOUNS,
  cellText,
  heatmapColumns,
  heatmapExportRows,
  heatmapSummary,
  sortHeatmapRows,
  thresholdScale,
} from '../lib/heatmap.js';
import { withFormats } from '../lib/table.js';
import {
  hours as formatHours,
  percent,
  weekLabel,
  longDate,
  shortDate,
  count,
} from '../lib/format.js';

const SORTS = [
  { key: 'label', label: 'Name' },
  { key: 'peak', label: 'Peak' },
];

/**
 * The capacity heatmap (PRD 10.3; Phase 23): allocation over capacity per row — people,
 * services, departments, offices, or the company — and per week, in the four reserved threshold
 * states, so overload can be scanned across a whole team at once. With the daily grain in the
 * filter bar, the 14-day window is drawn per weekday instead, each week's five days beside the
 * weekly cell they sum to, and a day someone is off is a day of zero capacity.
 *
 * Everything drawn here comes from `GET /forecast/heatmap`; the component adds no arithmetic
 * beyond counting and ordering what it was given (`lib/heatmap.js`), so a cell is the API's
 * figure and the table exports the same numbers. The company row at the foot is the same total
 * the ribbon and the bridge were checked against, whatever the dimension.
 *
 * Encoding (PRD 7.4): a cell wears its band's reserved colour with the band's icon and the
 * percentage in an ink chosen for contrast on that colour; the word is in the legend, the
 * tooltip and the table. No project type is coloured here — the heatmap is drawn in the status
 * vocabulary alone. A cell with no capacity is hatched grey and prints the hours that have
 * nowhere to go.
 *
 * Rows are keyed (`repeat` on the row key), so a window or mode change updates the surviving
 * rows in place rather than repainting them.
 */
class CapacityHeatmap extends StoreMixin(LitElement) {
  static properties = {
    // The week the bullet rows below are drawn for (Phase 24): this card marks its column and
    // sets it when a column head is clicked; the page owns the value.
    week: { type: String },
    _data: { state: true },
    _loading: { state: true },
    _error: { state: true },
    _sort: { state: true },
    _hover: { state: true },
    _tableOpen: { state: true },
  };

  constructor() {
    super();
    this.week = null;
    this._data = null;
    this._loading = false;
    this._error = null;
    this._sort = 'label';
    this._hover = null;
    this._tableOpen = false;
    this._requestKey = null;
  }

  static styles = [
    PRINT_STYLES,
    css`
      :host {
        display: block;
      }
      .card {
        background: var(--surface);
        border: 1px solid var(--border-soft);
        border-radius: var(--radius-card);
        box-shadow: var(--shadow-card);
        padding: 20px 24px 18px;
      }
      .head {
        display: flex;
        flex-wrap: wrap;
        align-items: baseline;
        gap: 6px 18px;
        margin-bottom: 12px;
      }
      .eyebrow {
        font-family: var(--font-eyebrow);
        font-size: 10px;
        font-weight: 700;
        letter-spacing: 0.16em;
        text-transform: uppercase;
        color: var(--ink-muted);
      }
      h2 {
        margin: 2px 0 0;
        font-size: 20px;
        line-height: 1.2;
        color: var(--ink-strong);
      }
      .sub {
        color: var(--ink-muted);
        font-size: 12.5px;
      }
      .kpis {
        margin-left: auto;
        display: flex;
        gap: 18px;
        flex-wrap: wrap;
      }
      .kpi {
        text-align: right;
      }
      .kpi .n {
        font-size: 18px;
        font-weight: 700;
        color: var(--ink-strong);
        font-variant-numeric: tabular-nums;
        line-height: 1.1;
      }
      .kpi .l {
        font-size: 11px;
        color: var(--ink-muted);
      }
      .controls {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 10px 22px;
        margin: 4px 0 12px;
      }
      .segmented {
        display: inline-flex;
        align-items: center;
        gap: 8px;
        font-size: 12px;
        color: var(--ink-muted);
      }
      .segmented span.group {
        display: inline-flex;
        border: 1px solid var(--control-border);
        border-radius: var(--radius-pill);
        background: var(--control-bg);
        padding: 2px;
      }
      .segmented button {
        font: inherit;
        font-size: 12px;
        font-weight: 600;
        color: var(--ink-muted);
        background: none;
        border: 0;
        border-radius: var(--radius-pill);
        padding: 3px 10px;
        cursor: pointer;
      }
      .segmented button[aria-pressed='true'] {
        color: var(--ink-on-brand);
        background: var(--ink-strong);
      }

      /* The threshold scale: each band drawn exactly as a cell is, with its range and its word. */
      .scale {
        display: flex;
        flex-wrap: wrap;
        gap: 6px 16px;
        list-style: none;
        margin: 0;
        padding: 0;
        font-size: 12.5px;
      }
      .scale li {
        display: inline-flex;
        align-items: center;
        gap: 7px;
      }
      .scale .sw {
        display: inline-grid;
        place-items: center;
        width: 22px;
        height: 16px;
        border-radius: 3px;
        font-size: 9px;
        font-weight: 700;
      }
      .scale .range {
        color: var(--ink-muted);
      }

      /* The matrix is the one thing here allowed to scroll sideways; the page never does. */
      .scroll {
        overflow-x: auto;
        border: 1px solid var(--border-soft);
        border-radius: var(--radius-card);
      }
      table.heat {
        border-collapse: separate;
        border-spacing: 0;
        width: 100%;
        font-size: 12.5px;
        font-variant-numeric: tabular-nums;
      }
      table.heat th,
      table.heat td {
        padding: 0;
        border-bottom: 1px solid var(--border-soft);
      }
      table.heat thead th {
        position: sticky;
        top: 0;
        z-index: 2;
        background: var(--surface-2);
        color: var(--ink-muted);
        font-weight: 600;
        font-size: 11.5px;
        text-align: center;
        padding: 6px 4px;
        white-space: nowrap;
        border-bottom: 1px solid var(--border);
      }
      table.heat thead tr.groups th {
        font-family: var(--font-eyebrow);
        font-size: 10px;
        letter-spacing: 0.1em;
        text-transform: uppercase;
        border-bottom: 1px solid var(--border-soft);
      }
      table.heat thead th.corner {
        text-align: left;
        padding-left: 12px;
      }
      table.heat thead th[data-week] button {
        font: inherit;
        color: inherit;
        background: none;
        border: 0;
        padding: 2px 4px;
        border-radius: 4px;
        cursor: pointer;
        white-space: nowrap;
      }
      table.heat thead th[data-week] button:hover {
        color: var(--link);
        text-decoration: underline;
      }
      /* The week the bullet rows below are drawn for: its column is marked, not recoloured. */
      table.heat thead th[data-selected='true'] button {
        color: var(--ink-strong);
        font-weight: 700;
        box-shadow: inset 0 -2px 0 var(--accent);
      }
      table.heat td[data-selected='true'] {
        background: var(--surface-2);
        box-shadow:
          inset 2px 0 0 var(--accent),
          inset -2px 0 0 var(--accent);
      }
      table.heat th[scope='row'],
      table.heat thead th.corner {
        position: sticky;
        left: 0;
        z-index: 3;
        background: var(--surface);
        text-align: left;
        min-width: 160px;
        max-width: 260px;
        padding: 4px 12px 4px 12px;
        font-weight: 500;
        color: var(--ink);
        border-right: 1px solid var(--border-soft);
      }
      table.heat thead th.corner {
        background: var(--surface-2);
      }
      table.heat th[scope='row'] button {
        font: inherit;
        color: inherit;
        background: none;
        border: 0;
        padding: 0;
        cursor: pointer;
        text-align: left;
        max-width: 100%;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        display: block;
      }
      table.heat th[scope='row'] button:hover {
        color: var(--link);
        text-decoration: underline;
      }
      table.heat tr[aria-selected='true'] th[scope='row'] {
        color: var(--ink-strong);
        font-weight: 700;
        box-shadow: inset 3px 0 0 var(--accent);
      }
      table.heat th[scope='row'] .small {
        display: block;
        font-size: 11px;
        color: var(--ink-muted);
        font-weight: 400;
      }
      table.heat th[scope='row'] .open {
        margin-left: 6px;
        font: inherit;
        font-size: 11px;
        font-weight: 600;
        color: var(--link);
        background: none;
        border: 0;
        padding: 0;
        cursor: pointer;
      }
      table.heat th[scope='row'] .open:hover {
        text-decoration: underline;
      }
      table.heat td {
        text-align: center;
        min-width: 58px;
        padding: 2px;
      }
      table.heat td.week {
        border-left: 2px solid var(--border);
        background: var(--surface-2);
      }
      table.heat td.gap {
        min-width: 6px;
        border-bottom: 0;
        background: var(--surface);
      }
      table.heat tfoot th,
      table.heat tfoot td {
        border-top: 2px solid var(--border);
        border-bottom: 0;
        background: var(--surface-2);
        font-weight: 700;
      }
      table.heat tfoot th[scope='row'] {
        background: var(--surface-2);
      }
      table.heat tr.unassigned th,
      table.heat tr.unassigned td {
        color: var(--ink-muted);
        font-style: italic;
      }
      table.heat tr.unassigned .cell {
        font-style: normal;
      }

      /* One cell: the band's colour, its icon, the number, in an ink that clears the colour. */
      .cell {
        display: inline-grid;
        grid-auto-flow: column;
        align-items: center;
        justify-content: center;
        gap: 4px;
        width: 100%;
        min-height: 30px;
        border-radius: 4px;
        padding: 0 6px;
        font-weight: 600;
        cursor: crosshair;
        outline-offset: -2px;
      }
      .cell .i {
        font-size: 9px;
        line-height: 1;
      }
      .cell[data-band='underload'] {
        background: var(--status-underload);
        color: var(--status-underload-ink);
      }
      .cell[data-band='healthy'] {
        background: var(--status-healthy);
        color: var(--status-healthy-ink);
      }
      .cell[data-band='caution'] {
        background: var(--status-caution);
        color: var(--status-caution-ink);
      }
      .cell[data-band='overloaded'] {
        background: var(--status-overloaded);
        color: var(--status-overloaded-ink);
      }
      .cell[data-band='none'] {
        background: repeating-linear-gradient(
          45deg,
          var(--surface-2),
          var(--surface-2) 3px,
          var(--border-soft) 3px,
          var(--border-soft) 6px
        );
        color: var(--ink-muted);
        font-weight: 500;
      }
      .cell.hours {
        background: var(--surface-2);
        color: var(--ink-muted);
        font-weight: 500;
        cursor: default;
      }
      .cell.hover {
        box-shadow: inset 0 0 0 2px var(--ink-strong);
      }

      .note {
        margin: 10px 0 0;
        font-size: 12.5px;
        color: var(--ink-muted);
      }
      .note.warn {
        color: var(--status-overloaded);
        font-weight: 600;
      }
      .state {
        padding: 36px 0;
        text-align: center;
        color: var(--ink-muted);
      }
      .state.error {
        color: var(--status-overloaded);
        font-weight: 600;
      }
      details.table {
        margin-top: 16px;
        border-top: 1px solid var(--border-soft);
        padding-top: 12px;
      }
      details.table summary {
        cursor: pointer;
        font-size: 13px;
        font-weight: 600;
        color: var(--link);
        list-style: none;
      }
      details.table summary::-webkit-details-marker {
        display: none;
      }
      details.table summary::before {
        content: '▸ ';
        color: var(--ink-muted);
      }
      details.table[open] summary::before {
        content: '▾ ';
      }
      details.table data-table {
        margin-top: 10px;
      }
      @media (max-width: 640px) {
        .card {
          padding: 16px 14px 14px;
        }
        table.heat th[scope='row'],
        table.heat thead th.corner {
          min-width: 120px;
        }
      }
    `,
  ];

  connectedCallback() {
    super.connectedCallback();
    // Fetch off the store, not off the render cycle: a filter or run change asks the API only
    // when a parameter this view reads has moved.
    this._unsubscribeFetch = store.subscribe(() => this._maybeFetch());
    this._maybeFetch();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this._unsubscribeFetch?.();
  }

  /** Whether the filter bar asks for the daily grain. */
  get _daily() {
    return this.appState.filters.grain === 'day';
  }

  /** The request this view wants for the current filters, or null when there is no run. */
  _params() {
    const { filters, run } = this.appState;
    if (!run) return null;
    const params = apiParams(filters, HEATMAP_PARAM_KEYS);
    if (!HEATMAP_DIMENSIONS.includes(params.dimension)) delete params.dimension;
    if (this._daily) {
      // The daily grain is the 14-day window from the start of the window, whatever length the
      // bar says: the API refuses more, and the rest of the window is the weekly grain's.
      params.grain = 'day';
      params.weeks = DAY_GRAIN_WEEKS;
    }
    return params;
  }

  async _maybeFetch() {
    const params = this._params();
    const key = params ? JSON.stringify(params) : null;
    if (key === this._requestKey) return;
    this._requestKey = key;
    if (!params) {
      this._data = null;
      this._publish();
      return;
    }
    this._loading = true;
    this._error = null;
    this._publish();
    try {
      const data = await api.get('/forecast/heatmap', params);
      if (this._requestKey !== key) return; // a newer request has taken over
      this._data = data;
    } catch (err) {
      if (this._requestKey !== key) return;
      this._error = err.message;
      this._data = null;
    } finally {
      if (this._requestKey === key) {
        this._loading = false;
        this._publish();
      }
    }
  }

  /**
   * Hand the response to whatever hosts this card, so the bullet rows (Phase 24) draw the
   * selected week of the same payload rather than asking the API for it a second time.
   */
  _publish() {
    // On a microtask: the first publish happens while this card is being inserted, and the host
    // sets state when it hears it, which would otherwise land inside the host's own render.
    const detail = { data: this._data, loading: this._loading, error: this._error };
    queueMicrotask(() =>
      this.dispatchEvent(
        new CustomEvent('heatmap-data', { detail, bubbles: true, composed: true }),
      ),
    );
  }

  // -------------------------------------------------------------------------------------------
  // Derived

  /** The daily grain is only drawn when the API answered it; otherwise weeks, with a note. */
  get _drawDays() {
    return Boolean(this._data?.grain === 'day' && this._data?.days?.length);
  }

  get _field() {
    return this._drawDays ? 'days' : 'cells';
  }

  get _rows() {
    return sortHeatmapRows(this._data?.rows ?? [], this._sort, this._field);
  }

  get _weeks() {
    return this._data?.totals ?? [];
  }

  get _nouns() {
    return ROW_NOUNS[this._data?.dimension] ?? ROW_NOUNS.company;
  }

  _summary() {
    return heatmapSummary(this._data, this._field);
  }

  // -------------------------------------------------------------------------------------------
  // Interaction

  _toggleEntity(row) {
    const { filters } = this.appState;
    const dimension = this._data?.dimension;
    if (!dimension || dimension === 'company') return;
    const already = filters.dimension === dimension && filters.entity === row.key;
    setFilters({ dimension, entity: already ? null : row.key });
  }

  _isSelected(row) {
    const { filters } = this.appState;
    return filters.dimension === this._data?.dimension && filters.entity === row.key;
  }

  _showTooltip(event, row, cell, kind) {
    const tooltip = this.renderRoot.querySelector('chart-tooltip');
    if (!tooltip) return;
    this._hover = `${row?.key ?? 'company'}|${kind}|${cell.date ?? cell.week_start}`;
    const rect = event.currentTarget?.getBoundingClientRect?.();
    const state = STATUS_STATES.find((s) => s.key === cell.band);
    const exact = (v) => formatHours(v, { exact: true });
    const rows = [
      { label: 'Allocated', value: exact(cell.allocated_hours) },
      { label: 'Capacity', value: exact(cell.capacity_hours) },
      cell.headroom_hours < 0
        ? { label: 'Over capacity', value: exact(-cell.headroom_hours) }
        : { label: 'Headroom', value: exact(cell.headroom_hours) },
    ];
    const split = [
      ['Committed (explicit)', cell.explicit_hours],
      ['Committed (inferred)', cell.inferred_hours],
      ['Recurring', cell.recurring_hours],
      ['Non-project', cell.nonproject_hours],
      ['Pipeline', cell.pipeline_hours],
    ].filter(([, v]) => v > 0);
    for (const [label, v] of split) rows.push({ label: `  ${label}`, value: exact(v) });
    if (cell.unassigned_hours > 0)
      rows.push({ label: '  of which role-only', value: exact(cell.unassigned_hours) });
    const facts = [
      cell.utilization === null ? 'No capacity' : `${percent(cell.utilization)} allocated`,
      state ? `${state.icon} ${state.label}` : null,
      `${count(cell.people)} people`,
      `${count(cell.projects)} projects`,
      cell.opportunities ? `${count(cell.opportunities)} deals` : null,
    ];
    if (kind === 'day') {
      if (cell.exception_hours > 0) facts.push(`${formatHours(cell.exception_hours)} time off`);
      if (cell.people_off > 0)
        facts.push(`${count(cell.people_off)} ${cell.people_off === 1 ? 'person' : 'people'} out`);
    }
    const who = row ? row.label : 'Whole company';
    const when = kind === 'day' ? longDate(cell.date) : `Week of ${longDate(cell.week_start)}`;
    tooltip.show({
      x: event.clientX ?? (rect ? rect.left + rect.width / 2 : 0),
      y: event.clientY ?? (rect ? rect.top : 0),
      title: `${who} · ${when}`,
      rows,
      footer: facts.filter(Boolean).join(' · '),
    });
  }

  _hideTooltip() {
    this._hover = null;
    this.renderRoot.querySelector('chart-tooltip')?.hide();
  }

  // -------------------------------------------------------------------------------------------
  // Matrix

  _cell(row, cell, kind) {
    const band = cell.band ?? 'none';
    const state = STATUS_STATES.find((s) => s.key === cell.band);
    const key = `${row?.key ?? 'company'}|${kind}|${cell.date ?? cell.week_start}`;
    const who = row ? row.label : 'Company';
    const when = kind === 'day' ? longDate(cell.date) : `week of ${weekLabel(cell.week_start)}`;
    const label =
      cell.utilization === null
        ? `${who}, ${when}: no capacity, ${formatHours(cell.allocated_hours)} allocated`
        : `${who}, ${when}: ${percent(cell.utilization)} allocated, ${state?.label ?? ''}, ${formatHours(cell.allocated_hours)} of ${formatHours(cell.capacity_hours)}`;
    return html`
      <span
        class=${`cell ${this._hover === key ? 'hover' : ''}`}
        data-band=${band}
        data-kind=${kind}
        data-period=${cell.date ?? cell.week_start}
        tabindex="0"
        role="img"
        aria-label=${label}
        @pointermove=${(e) => this._showTooltip(e, row, cell, kind)}
        @pointerleave=${() => this._hideTooltip()}
        @focus=${(e) => this._showTooltip(e, row, cell, kind)}
        @blur=${() => this._hideTooltip()}
      >
        ${state ? html`<span class="i" aria-hidden="true">${state.icon}</span>` : nothing}
        <span class="v">${cellText(cell)}</span>
      </span>
    `;
  }

  /** The cells of one row across the columns: days with the weekly cell after each week, or weeks. */
  _rowCells(row, weeklyCells, dayCells) {
    const mark = (c) => (this.week === c.week_start ? 'true' : 'false');
    if (!this._drawDays)
      return weeklyCells.map(
        (c) => html`<td data-selected=${mark(c)}>${this._cell(row, c, 'week')}</td>`,
      );
    const out = [];
    for (const w of weeklyCells) {
      for (const d of dayCells.filter((x) => x.week_start === w.week_start))
        out.push(html`<td data-selected=${mark(d)}>${this._cell(row, d, 'day')}</td>`);
      out.push(html`<td class="week" data-selected=${mark(w)}>${this._cell(row, w, 'week')}</td>`);
    }
    return out;
  }

  _hoursCell(value, label) {
    return html`<span class="cell hours" role="img" aria-label=${label}
      >${formatHours(value)}</span
    >`;
  }

  /** A column head that names its week and selects it for the bullet rows beneath the matrix. */
  _weekHead(week, label, { scope = 'col', colspan = 1 } = {}) {
    const selected = this.week === week.week_start;
    return html`<th
      scope=${scope}
      colspan=${colspan}
      data-week=${week.week_start}
      data-selected=${selected ? 'true' : 'false'}
    >
      <button
        type="button"
        aria-pressed=${selected ? 'true' : 'false'}
        title=${`Draw the bullet rows for the week of ${longDate(week.week_start)}`}
        @click=${() => this._selectWeek(week.week_start)}
      >
        ${label}
      </button>
    </th>`;
  }

  /** The week the bullet rows draw. Announced, never kept: the page owns it (see pages.js). */
  _selectWeek(weekStart) {
    this.dispatchEvent(
      new CustomEvent('heatmap-week', {
        detail: { week_start: weekStart },
        bubbles: true,
        composed: true,
      }),
    );
  }

  _head() {
    const d = this._data;
    const [noun] = this._nouns;
    if (!this._drawDays)
      return html`
        <thead>
          <tr>
            <th class="corner" scope="col">${noun}</th>
            ${this._weeks.map((w) => this._weekHead(w, weekLabel(w.week_start)))}
          </tr>
        </thead>
      `;
    return html`
      <thead>
        <tr class="groups">
          <th class="corner" scope="col" rowspan="2">${noun}</th>
          ${this._weeks.map((w) =>
            this._weekHead(w, `Week of ${weekLabel(w.week_start)}`, {
              scope: 'colgroup',
              colspan: d.days.filter((x) => x.week_start === w.week_start).length + 1,
            }),
          )}
        </tr>
        <tr>
          ${this._weeks.map(
            (w) => html`
              ${d.days
                .filter((x) => x.week_start === w.week_start)
                .map(
                  (x) =>
                    html`<th scope="col" title=${longDate(x.date)}>
                      ${x.weekday} ${shortDate(x.date).split(' ')[1]}
                    </th>`,
                )}
              <th scope="col" class="week" title="The weekly cell these days sum to">Week</th>
            `,
          )}
        </tr>
      </thead>
    `;
  }

  _matrix() {
    const d = this._data;
    const rows = this._rows;
    const dimension = d.dimension;
    const totalsDays = d.totals_days ?? [];
    const individual = dimension === 'individual';
    const unassignedWeeks = this._weeks.some((t) => t.unassigned_hours > 0);
    return html`
      <div class="scroll">
        <table class="heat" aria-label=${`Allocation over capacity by ${this._nouns[1]}`}>
          ${this._head()}
          <tbody>
            ${repeat(
              rows,
              (row) => row.key,
              (row) => html`
                <tr data-key=${row.key} aria-selected=${this._isSelected(row) ? 'true' : 'false'}>
                  <th scope="row">
                    ${
                      dimension === 'company'
                        ? html`<span>${row.label}</span>`
                        : html`<button
                            type="button"
                            title=${this._isSelected(row) ? 'Clear the entity filter' : `Filter the views to ${row.label}`}
                            @click=${() => this._toggleEntity(row)}
                          >
                            ${row.label}
                          </button>`
                    }
                    ${
                      individual
                        ? html`<button
                            type="button"
                            class="open"
                            title=${`Open ${row.label}'s weeks (the individual view)`}
                            @click=${() => navigate(`/people/${row.key}`)}
                          >
                            weeks →
                          </button>`
                        : nothing
                    }
                    ${
                      individual && row.entity?.department
                        ? html`<span class="small">${row.entity.department}</span>`
                        : nothing
                    }
                  </th>
                  ${this._rowCells(row, row.cells, row.days ?? [])}
                </tr>
              `,
            )}
            ${
              individual && unassignedWeeks
                ? html`
                    <tr class="unassigned" data-key="unassigned">
                      <th scope="row">
                        <span>Role-only (nobody named)</span>
                        <span class="small">hours, not a ratio: no person, no capacity</span>
                      </th>
                      ${
                        this._drawDays
                          ? this._weeks.flatMap((w) => [
                              ...totalsDays
                                .filter((x) => x.week_start === w.week_start)
                                .map(
                                  (x) =>
                                    html`<td>
                                      ${this._hoursCell(x.unassigned_hours, `Role-only hours on ${longDate(x.date)}`)}
                                    </td>`,
                                ),
                              html`<td class="week">
                                ${this._hoursCell(w.unassigned_hours, `Role-only hours in the week of ${weekLabel(w.week_start)}`)}
                              </td>`,
                            ])
                          : this._weeks.map(
                              (w) =>
                                html`<td>
                                  ${this._hoursCell(w.unassigned_hours, `Role-only hours in the week of ${weekLabel(w.week_start)}`)}
                                </td>`,
                            )
                      }
                    </tr>
                  `
                : nothing
            }
          </tbody>
          ${
            dimension === 'company'
              ? nothing
              : html`
                  <tfoot>
                    <tr data-key="company">
                      <th scope="row">Company</th>
                      ${this._rowCells(null, this._weeks, totalsDays)}
                    </tr>
                  </tfoot>
                `
          }
        </table>
      </div>
    `;
  }

  // -------------------------------------------------------------------------------------------
  // Chrome

  _scale() {
    const scale = thresholdScale(this._data?.thresholds);
    return html`
      <ul class="scale" aria-label="Threshold colour scale">
        ${scale.map(
          (s) => html`
            <li>
              <span
                class="sw"
                style=${`background: var(${s.var}); color: var(${s.var}-ink);`}
                aria-hidden="true"
                >${s.icon}</span
              >
              <span>${s.label}</span>
              <span class="range">${s.range}</span>
            </li>
          `,
        )}
        <li>
          <span class="sw cell" data-band="none" aria-hidden="true">—</span>
          <span>No capacity</span>
          <span class="range">hours shown</span>
        </li>
      </ul>
    `;
  }

  _subtitle() {
    const d = this._data;
    const { filters } = this.appState;
    if (!d) return '';
    const notes = [];
    if (!HEATMAP_DIMENSIONS.includes(filters.dimension))
      notes.push('the heatmap has no project dimension, so the company is shown');
    if (this._drawDays)
      notes.push(
        `daily grain: the ${d.days.length} weekdays of the 14-day window from ${weekLabel(d.from)}, each week's days beside the weekly cell they sum to`,
      );
    else if (filters.grain !== 'week')
      notes.push(`weekly grain (the heatmap has no ${filters.grain} grain yet)`);
    const rowsOf =
      d.dimension === 'company' ? 'the company' : `${count(d.rows.length)} ${this._nouns[1]}`;
    return `${rowsOf} · ${d.weeks_count} weeks from ${weekLabel(d.from)} · pipeline ${d.pipeline_mode}${
      notes.length ? ` · ${notes.join('; ')}` : ''
    }`;
  }

  _kpis() {
    const d = this._data;
    if (!d) return nothing;
    const s = this._summary();
    const cells = Object.values(s.per_band).reduce((a, b) => a + b, 0) + s.no_capacity;
    const unit = this._drawDays ? 'days' : 'weeks';
    const peakWhen = s.peak
      ? this._drawDays
        ? shortDate(s.peak.cell.date)
        : `week of ${weekLabel(s.peak.cell.week_start)}`
      : '';
    return html`
      <div class="kpis">
        <div class="kpi">
          <div class="n">${count(s.per_band.overloaded)}</div>
          <div class="l">
            of ${count(cells)} ${unit === 'days' ? 'day' : 'week'} cells overloaded
          </div>
        </div>
        <div class="kpi">
          <div class="n">${count(s.per_band.underload)}</div>
          <div class="l">under-utilized</div>
        </div>
        <div class="kpi">
          <div class="n">${s.peak ? percent(s.peak.cell.utilization) : '—'}</div>
          <div class="l">
            peak${s.peak ? `, ${d.dimension === 'company' ? '' : `${s.peak.row.label}, `}${peakWhen}` : ''}
          </div>
        </div>
      </div>
    `;
  }

  _notes() {
    const d = this._data;
    const { filters, scope } = this.appState;
    if (!d) return nothing;
    const notes = [];
    if (d.withheld_count > 0)
      notes.push(
        html`<p class="note" data-note="withheld">
          ${count(d.withheld_count)} ${d.withheld_count === 1 ? 'person is' : 'people are'} not
          shown: your access covers
          ${scope?.unrestricted ? 'everyone' : 'yourself and your reports'} at the individual grain
          (the company row still counts them).
        </p>`,
      );
    if (filters.entity && filters.dimension === d.dimension) {
      const row = d.rows.find((r) => r.key === filters.entity);
      notes.push(
        html`<p class="note" data-note="entity">
          ${row ? `${row.label} is highlighted;` : 'The entity in the filter bar is not a row here;'}
          every ${this._nouns[1] === 'company' ? 'row' : this._nouns[1].replace(/s$/, '')} stays on
          the heatmap so it can be compared. Click the name again to clear the filter.
        </p>`,
      );
    }
    if (this._drawDays)
      notes.push(
        html`<p class="note" data-note="daily">
          Days are a projection of the run's weekly rows (TRD 9): a person's week is shaped by the
          calendar as it stands today, so a day off is zero capacity and its work moves to their
          other days; each row's hours are spread over the weekdays its window covers. The five days
          of a week always add up to the weekly cell beside them.
        </p>`,
      );
    return notes;
  }

  // -------------------------------------------------------------------------------------------
  // Table

  get _columns() {
    // The specification is shared with the server's CSV (lib/heatmap.js); only the drawing is
    // here, so an export cannot hold a different set of columns from the table it came from.
    const daily = this._drawDays;
    return withFormats(
      heatmapColumns({ dimension: this._data?.dimension, grain: daily ? 'day' : 'week' }),
      {
        byUnit: {
          hours: (v) => (v === null || v === undefined ? '—' : formatHours(v, { exact: true })),
          percent: (v) => percent(v),
        },
        byKey: {
          period: (v) => (daily ? longDate(v) : weekLabel(v)),
          band: (v) => (v ? html`<status-badge state=${v} compact></status-badge>` : '—'),
        },
      },
    );
  }

  get _exportRows() {
    return heatmapExportRows(this._data, this._drawDays ? 'day' : 'week');
  }

  // -------------------------------------------------------------------------------------------
  // Render

  render() {
    const { run } = this.appState;
    const d = this._data;

    let body;
    if (!run) body = html`<div class="state">No forecast run yet — nothing to draw.</div>`;
    else if (this._error) body = html`<div class="state error">${this._error}</div>`;
    else if (!d && this._loading) body = html`<div class="state">Loading the heatmap…</div>`;
    else if (!d || !d.totals.length) body = html`<div class="state">No weeks in this window.</div>`;
    else
      body = html`
        <div class="controls">
          <div class="segmented" role="group" aria-label="Row order">
            <span>Sort</span>
            <span class="group">
              ${SORTS.map(
                (s) =>
                  html`<button
                    type="button"
                    aria-pressed=${this._sort === s.key ? 'true' : 'false'}
                    @click=${() => (this._sort = s.key)}
                  >
                    ${s.label}
                  </button>`,
              )}
            </span>
          </div>
          ${this._scale()}
        </div>
        ${this._matrix()} ${this._notes()}
        <details
          class="table"
          ?open=${this._tableOpen}
          @toggle=${(e) => (this._tableOpen = e.target.open)}
        >
          <summary>Table view</summary>
          <data-table
            caption=${`Allocation over capacity per ${this._nouns[1] === 'company' ? 'week' : `${this._nouns[0].toLowerCase()} and ${this._drawDays ? 'day' : 'week'}`} — ${this._subtitle()}`}
            export-name=${this._drawDays ? 'capacity-heatmap-days' : 'capacity-heatmap'}
            page-size="25"
            dense
            .columns=${this._columns}
            .rows=${this._exportRows}
          ></data-table>
        </details>
      `;

    return html`
      <div class="card" aria-busy=${this._loading ? 'true' : 'false'}>
        <div class="head">
          <div>
            <div class="eyebrow">Capacity heatmap</div>
            <h2>Who is over capacity, and in which week?</h2>
            <div class="sub">${this._subtitle()}</div>
          </div>
          ${this._kpis()}
        </div>
        ${body}
        <chart-tooltip></chart-tooltip>
      </div>
    `;
  }
}

customElements.define('capacity-heatmap', CapacityHeatmap);
